import React, { useState } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import { Upload } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import styled from 'styled-components';

import { updateShopRequestCommunicationLogActivity } from 'apis/ActivitiesAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import {
  buildCommunicationLogFilePayload,
  getFileSizeLimitErrorMessage,
  isFileWithinSizeLimit,
} from 'components/MysteryShopping/EditShopRequestModal/helpers';
import { CommunityActivityType } from 'types/communities';

import ActivityAttachments from './ActivityAttachments';
import { getRingCentralActivityAttachments } from './ActivityRingCentralMedia';
import { buildRenderedEmailDocument } from './utils/renderedEmail';

const CALL_CONNECTED_ACTIVITY_TYPE = 'call_connected';

type ActivityContentProps = {
  activity: CommunityActivityType;
  communityId?: number;
  showRenderedBody: boolean;
  hideSummary?: boolean;
  onActivityUpdated?: () => void | Promise<unknown>;
};

const Title = styled.div`
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 15px;
  word-break: break-word;
`;

const Actor = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  word-break: break-word;
`;

const Body = styled.div`
  color: var(--text-primary);
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const UploadTranscriptButton = styled(Button)`
  align-self: flex-start;
  height: auto;
  padding: 0;
  color: var(--dark-blue);
  font-family: var(--font-bold);
  font-size: 12px;
  box-shadow: none;

  &.ant-btn-link:not(:disabled):hover {
    color: var(--dark-blue);
  }
`;

const UploadTranscriptAction = styled.div`
  display: flex;
  align-self: flex-start;
  margin-left: auto;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;

  ${UploadTranscriptButton}:focus-visible {
    outline: 2px solid var(--dark-blue);
    outline-offset: 2px;
  }
`;

const AttachmentActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const RenderedBodyFrame = styled.iframe`
  width: 100%;
  max-width: 100%;
  height: 320px;
  border: 0;
  border-radius: 10px;
  background: #ffffff;
  box-sizing: border-box;
`;

const ActivityContent = ({
  activity,
  communityId,
  showRenderedBody,
  hideSummary = false,
  onActivityUpdated,
}: ActivityContentProps) => {
  const { showMessage } = useRootContext();
  const [isUploadingTranscript, setIsUploadingTranscript] = useState(false);
  const textBody = activity.email?.display_text_body || activity.email?.text_body;
  const renderedHtmlBody = activity.email?.display_html_body || activity.email?.html_body;
  const bodyText = textBody || activity.body;
  const canUploadTranscript = activity.metadata?.activity_type === CALL_CONNECTED_ACTIVITY_TYPE;
  const attachments = [
    ...activity.attachments,
    ...getRingCentralActivityAttachments(activity.ringcentral_communication),
  ];
  const hasContent = Boolean(
    (showRenderedBody && renderedHtmlBody) || bodyText || attachments.length || (canUploadTranscript && communityId)
  );

  const uploadTranscript = async (file: File) => {
    if (!communityId || isUploadingTranscript) {
      return Upload.LIST_IGNORE;
    }

    if (!isFileWithinSizeLimit(file)) {
      showMessage('error', getFileSizeLimitErrorMessage(file.name));
      return Upload.LIST_IGNORE;
    }

    setIsUploadingTranscript(true);
    try {
      const files = [{ name: file.name, originFileObj: file } as UploadFile];
      const filePayload = await buildCommunicationLogFilePayload(files);
      await updateShopRequestCommunicationLogActivity(communityId, activity.id, { files: filePayload });
      await onActivityUpdated?.();
      showMessage('success', 'Communication Log updated.');
    } catch (error) {
      showMessage('error', 'Failed to upload transcript.');
      console.error('Failed to upload transcript.', error);
    } finally {
      setIsUploadingTranscript(false);
    }

    return Upload.LIST_IGNORE;
  };

  if (!hasContent) {
    return null;
  }

  return (
    <>
      {!hideSummary && (
        <>
          <Title>{activity.title}</Title>
          {activity.actor?.name && <Actor>Recorded by {activity.actor.name}</Actor>}
        </>
      )}

      {showRenderedBody && renderedHtmlBody ? (
        <RenderedBodyFrame
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={buildRenderedEmailDocument(renderedHtmlBody)}
          title={`Rendered activity ${activity.id}`}
        />
      ) : (
        bodyText && <Body>{bodyText}</Body>
      )}

      <AttachmentActions>
        <ActivityAttachments attachments={attachments} />

        {canUploadTranscript && communityId && (
          <UploadTranscriptAction className="upload-transcript-action">
            <Upload
              showUploadList={false}
              maxCount={1}
              disabled={isUploadingTranscript}
              beforeUpload={uploadTranscript}
            >
              <UploadTranscriptButton type="link" loading={isUploadingTranscript} icon={<UploadOutlined />}>
                Upload Transcript
              </UploadTranscriptButton>
            </Upload>
          </UploadTranscriptAction>
        )}
      </AttachmentActions>
    </>
  );
};

export default ActivityContent;
